import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ConfirmDialog, Popup, Button } from '@ui';
import { Tooltip } from '@ui/atoms/Tooltip';
import { translate } from '@utils/i18n';

const namespace = 'features.settings.tax_items.index';

function ActionButtons({ taxItem, onSwitch, onDelete }) {
  const [toggleDialogVisible, setToggleDialogVisible] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const anchor = useRef(null);

  const canToggle = !taxItem.hidden;

  const toggleLabel = taxItem.active ? translate('deactivate', { namespace }) : translate('activate', { namespace });

  return (
    <span data-allow-click style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
      <Button ref={anchor} aria-label={translate('actions', { namespace })} onClick={() => setPopupOpen((p) => !p)}>
        <i className="fa-regular fa-ellipsis" />
      </Button>

      <Popup
        anchor={anchor.current}
        show={popupOpen}
        onClose={() => setPopupOpen(false)}
        position="bottom-left"
        collision={{ horizontal: 'fit', vertical: 'flip' }}>
        <Popup.Section>
          <Popup.ActionList>
            {canToggle ? (
              <Button
                onClick={() => {
                  setPopupOpen(false);
                  setToggleDialogVisible(true);
                }}>
                <i className={`fa-light ${taxItem.active ? 'fa-ban' : 'fa-check-circle'}`} />
                {toggleLabel}
              </Button>
            ) : (
              <Tooltip content={translate('cannot_deactivate', { namespace })} position="left">
                <span>
                  <Button disabled>
                    <i className={`fa-light ${taxItem.active ? 'fa-ban' : 'fa-check-circle'}`} />
                    {toggleLabel}
                  </Button>
                </span>
              </Tooltip>
            )}

            {taxItem.canDestroy ? (
              <Button
                style={{ color: 'red' }}
                onClick={() => {
                  setPopupOpen(false);
                  setDeleteDialogVisible(true);
                }}>
                <i className="fa-light fa-trash-can" />
                {translate('delete', { namespace })}
              </Button>
            ) : (
              <Tooltip content={translate('cannot_delete', { namespace })} position="left">
                <span>
                  <Button disabled>
                    <i className="fa-light fa-trash-can" />
                    {translate('delete', { namespace })}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Popup.ActionList>
        </Popup.Section>
      </Popup>

      <ConfirmDialog
        isOpen={toggleDialogVisible}
        title={translate(taxItem.active ? 'deactivate_modal_title' : 'activate_modal_title', { namespace })}
        description={translate(taxItem.active ? 'deactivate_modal_body' : 'activate_modal_body', { namespace })}
        confirmLabel={toggleLabel}
        cancelLabel={translate('cancel', { namespace: 'generic' })}
        loading={toggleLoading}
        onConfirm={() => {
          setToggleLoading(true);
          onSwitch().finally(() => {
            setToggleLoading(false);
            setToggleDialogVisible(false);
          });
        }}
        onCancel={() => setToggleDialogVisible(false)}
      />

      <ConfirmDialog
        isOpen={deleteDialogVisible}
        title={translate('delete_modal_title', { namespace })}
        description={translate('delete_modal_body', { namespace })}
        confirmLabel={translate('delete', { namespace })}
        cancelLabel={translate('cancel', { namespace: 'generic' })}
        confirmVariant="danger"
        loading={deleteLoading}
        onConfirm={() => {
          setDeleteLoading(true);
          onDelete().finally(() => {
            setDeleteLoading(false);
            setDeleteDialogVisible(false);
          });
        }}
        onCancel={() => setDeleteDialogVisible(false)}
      />
    </span>
  );
}

ActionButtons.propTypes = {
  taxItem: PropTypes.object.isRequired,
  onSwitch: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default ActionButtons;
